import { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

type SearchableOption = {
  id: number;
  name: string;
};

type Props = {
  label: string;
  options: SearchableOption[];
  selectedId: number | undefined;
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  onSelect: (id: number) => void;
};

export const SearchableSelect = ({
  label,
  options,
  selectedId,
  disabled = false,
  placeholder,
  searchPlaceholder,
  emptyText,
  onSelect,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((item) => item.id === selectedId);

  const filteredOptions = useMemo(() => {
    const normalized = query.trim();
    if (!normalized) {
      return options;
    }
    return options.filter((item) => item.name.includes(normalized));
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) {
          close();
        }
      }}
    >
      <span className="text-sm font-semibold text-zinc-200">{label}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={`mt-2 flex h-12 w-full items-center justify-between gap-3 rounded-sm border bg-zinc-950 px-3 text-right text-sm outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 ${
          open ? "border-teal-400 ring-2 ring-teal-400/20" : "border-zinc-700"
        } disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selected ? "font-semibold text-white" : "text-zinc-500"}`}>
          {selected?.name ?? placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-zinc-400 transition ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open ? (
        <div className="absolute right-0 z-20 mt-1 w-full rounded-md border border-zinc-700 bg-zinc-950 p-2 shadow-2xl shadow-black/40">
          <div className="flex h-10 items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-900 px-2 focus-within:border-teal-400 focus-within:bg-zinc-950">
            <Search className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
              autoFocus
            />
          </div>

          <div className="mt-2 max-h-56 overflow-y-auto" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const active = option.id === selectedId;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onSelect(option.id);
                      close();
                    }}
                    className={`flex min-h-9 w-full items-center justify-between gap-3 rounded-sm px-2 py-2 text-right text-sm transition ${
                      active ? "bg-teal-400 font-bold text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"
                    }`}
                    role="option"
                    aria-selected={active}
                  >
                    <span className="min-w-0 flex-1">{option.name}</span>
                    {active ? <Check className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
                  </button>
                );
              })
            ) : (
              <p className="px-2 py-3 text-sm text-zinc-500">{emptyText}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
